import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from '@tanstack/react-router'
import { Pencil, Trash2, Printer } from 'lucide-react'
import { useRecipe } from './useRecipe'
import { useDeleteRecipe } from './useDeleteRecipe'
import { useToggleFavourite } from './useToggleFavourite'
import { FavouriteToggle } from './FavouriteToggle'
import { Dialog } from '@/shared/components/Dialog'

export function RecipeDetail() {
  const { recipeId } = useParams({ strict: false }) as { recipeId: string }
  const { t } = useTranslation('recipes')
  const { data: recipe, isLoading, isError } = useRecipe(recipeId)
  const deleteMutation = useDeleteRecipe()
  const toggleMutation = useToggleFavourite()
  const [deleteOpen, setDeleteOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="px-4 pt-4 pb-24 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-8 rounded-lg bg-surface animate-pulse" />
        ))}
      </div>
    )
  }

  if (isError || !recipe) {
    return (
      <div className="px-4 pt-4 pb-24">
        <p className="text-danger text-sm" role="alert">{t('detail.error')}</p>
      </div>
    )
  }

  const handleDelete = () => {
    setDeleteOpen(false)
    deleteMutation.mutate(recipeId)
  }

  return (
    <div className="px-4 pt-4 pb-24">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold text-ink">{recipe.name}</h1>
          <p className="text-sm text-muted mt-1">{t('detail.servesLabel', { count: recipe.servingSize })}</p>
        </div>
        <div className="flex items-center gap-1 print:hidden">
          <FavouriteToggle
            recipeId={recipeId}
            name={recipe.name}
            isFavourite={recipe.isFavourite}
            isPending={toggleMutation.isPending}
            onToggle={(id, fav) => toggleMutation.mutate({ recipeId: id, isFavourite: fav })}
          />
          <Link
            to="/recipes/$recipeId/edit"
            params={{ recipeId }}
            aria-label={t('detail.editAriaLabel')}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] text-accent rounded-lg hover:bg-accent-tint print:hidden"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            aria-label={t('detail.deleteAriaLabel')}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] text-danger rounded-lg hover:bg-red-50 print:hidden"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            aria-label={t('print.buttonLabel')}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] text-muted rounded-lg hover:bg-surface print:hidden"
          >
            <Printer className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      <section className="mb-6">
        <h2 className="text-base font-semibold text-ink mb-3">{t('detail.ingredientsHeading')}</h2>
        <ul className="space-y-1">
          {recipe.ingredients.map((ing) => (
            <li key={ing.id} className="text-sm text-ink">
              {ing.quantity} {ing.unit} {ing.ingredientName}
            </li>
          ))}
        </ul>
      </section>

      {recipe.method && (
        <section>
          <h2 className="text-base font-semibold text-ink mb-3">{t('detail.methodHeading')}</h2>
          <p className="text-sm text-ink whitespace-pre-wrap">{recipe.method}</p>
        </section>
      )}

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t('delete.confirmTitle')}
      >
        <p className="text-sm text-muted mb-6">{t('delete.confirmMessage', { name: recipe.name })}</p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => setDeleteOpen(false)}
            className="min-h-[44px] px-4 rounded-lg border border-line text-sm font-medium text-ink"
          >
            {t('delete.cancelButton')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="min-h-[44px] px-4 rounded-lg bg-danger text-white text-sm font-medium"
          >
            {t('delete.confirmButton')}
          </button>
        </div>
      </Dialog>
    </div>
  )
}
